import React, { useMemo, useCallback } from 'react';
import { v4 } from 'uuid';
import Dialog from 'components/shared/Dialog';
import styles from './GA2RFValuesModal.module.css';
import { getGa2DeviceMembers } from 'common/utils/deviceTwinUtils';
import {
  azElColumns,
  azElDataParser,
  linkParameterColumns,
  linkParameterDataParser
} from './components/GA2RFValuesTable/utils';
import GA2RFValuesTable from './components/GA2RFValuesTable/GA2RFValuesTable';
import { findLatestLastUpdatedValue, getLatestRfPathTime } from 'common/utils/helpers';
import UpdateButton from 'components/shared/UpdateButton';
import { useRFValues } from './hooks';
import { useDispatch } from 'react-redux';
import { addToastNotification } from 'redux/actions';

function GA2RFValuesModal({ device, onClose, handleUpdate }) {
  const dispatch = useDispatch();
  const ga2DeviceMembers = useMemo(() => getGa2DeviceMembers(device, false), [device]);

  const allMembers = useMemo(() => [device?.properties?.reported?.DU, ...ga2DeviceMembers], [device, ga2DeviceMembers]);

  const azElData = useMemo(() => azElDataParser(allMembers), [allMembers]);
  const azElCols = useMemo(() => azElColumns(ga2DeviceMembers), [ga2DeviceMembers]);

  const uplinkParameterData = useMemo(() => linkParameterDataParser(ga2DeviceMembers, true), [ga2DeviceMembers]);
  const uplinkParameterCols = useMemo(() => linkParameterColumns(ga2DeviceMembers, true), [ga2DeviceMembers]);

  const downlinkParameterData = useMemo(() => linkParameterDataParser(ga2DeviceMembers, false), [ga2DeviceMembers]);
  const downlinkParameterCols = useMemo(() => linkParameterColumns(ga2DeviceMembers, false), [ga2DeviceMembers]);

  const latestAntennaLastUpdated = useMemo(() => findLatestLastUpdatedValue(device, 'az-el-info'), [device]);
  const latestUplinkLastUpdated = useMemo(() => getLatestRfPathTime(device?.properties?.reported?.$metadata, 'uplink'), [device]);
  const latestDownlinkLastUpdated = useMemo(() => getLatestRfPathTime(device?.properties?.reported?.$metadata, 'downlink'), [device]);

  const handleRFValuesSuccess = useCallback(
    (_, variables) => {
      handleUpdate(variables.deviceId);
      dispatch(
        addToastNotification({
          id: v4(),
          title: variables.deviceId,
          description: 'has been synchronized.'
        })
      );
    },
    [dispatch, handleUpdate]
  );
  const handleRFValuesError = useCallback(
    (_, variables) => {
      dispatch(
        addToastNotification({
          id: v4(),
          title: variables.deviceId,
          description: 'Failed to synchronize',
          reverse: true
        })
      );
    },
    [dispatch]
  );

  const rfValues = useRFValues(device, {
    onSuccess: handleRFValuesSuccess,
    onError: handleRFValuesError
  });

  const { refreshing, onRefresh } = rfValues.dialog;

  return (
    <Dialog open={true} onClose={onClose}>
      <Dialog.Container maxWidth={1320} maxHeight={900}>
        <Dialog.Title className={styles.titleContainer}>
          <h3 className={styles.title}>RF Values</h3>
          <UpdateButton className={styles.refresh} updating={refreshing} onClick={onRefresh}>
            Refresh
          </UpdateButton>
        </Dialog.Title>
        <Dialog.Content>
          <div className={styles.content}>
            <div className={styles.topContent}>
              <GA2RFValuesTable data={azElData} columns={azElCols} lastUpdated={latestAntennaLastUpdated} />
            </div>
            <div className={styles.bottomContent}>
              <GA2RFValuesTable
                data={uplinkParameterData}
                columns={uplinkParameterCols}
                lastUpdated={latestUplinkLastUpdated}
              />
              <GA2RFValuesTable
                data={downlinkParameterData}
                columns={downlinkParameterCols}
                lastUpdated={latestDownlinkLastUpdated}
              />
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Close />
      </Dialog.Container>
    </Dialog>
  );
}

export default GA2RFValuesModal;
